import React, { useState, useEffect, useRef } from 'react';
import { Search, MapPin, X } from 'lucide-react';
import axios from 'axios';
import { BASEURL } from '../constants';

function SuburbSearch({ 
  value, 
  onSelect, 
  placeholder = "Search suburb...",
  disabled = false 
}) {
  const [searchText, setSearchText] = useState('');
  const [suburbs, setSuburbs] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const justSelectedRef = useRef(false);
  const selectedSuburbRef = useRef('');
  const initializedRef = useRef(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

    // Initialize with value prop when it changes (from API)
    useEffect(() => {
    if (value && !initializedRef.current) {
        setSearchText(value);
        selectedSuburbRef.current = value;
        justSelectedRef.current = true;
        initializedRef.current = true;
    }
    }, [value]);

    // Reset initialization when value changes externally
    useEffect(() => {
    if (value && value !== searchText && value !== selectedSuburbRef.current) {
        setSearchText(value);
        selectedSuburbRef.current = value;
        justSelectedRef.current = true;
    }
    }, [value]);

  // Debounced search
  useEffect(() => {
    if (searchText.length < 2) {
      setSuburbs([]);
      setIsOpen(false);
      return;
    }

    // Don't search if we just selected this value
    if (justSelectedRef.current && searchText === selectedSuburbRef.current) {
      justSelectedRef.current = false; // Reset the flag
      return;
    }

    // If user is typing something different from the selected value, allow search
    if (searchText !== selectedSuburbRef.current) {
      justSelectedRef.current = false;
      selectedSuburbRef.current = '';
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${BASEURL}suburb/getsuburb?searchText=${encodeURIComponent(searchText)}`
        );
        setSuburbs(response?.data?.slice(0, 6));
        setIsOpen(true);
      } catch (error) {
        console.error('Error fetching suburbs:', error);
        setSuburbs([]);
      } finally {
        setLoading(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchText]);

  const handleSelect = (suburb) => {
    justSelectedRef.current = true; 
    selectedSuburbRef.current = suburb.name;
    setSearchText(suburb.name);
    setSuburbs([]); 
    setIsOpen(false);
    onSelect({
      suburbID: suburb.suburbID,
      suburb: suburb.name,
      state: suburb.stateAbbr,
      postCode: suburb.postCode,
      country: suburb.country
    });
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchText(newValue);
    
    // If user is editing, reset the selection
    if (newValue !== selectedSuburbRef.current) {
      justSelectedRef.current = false;
      selectedSuburbRef.current = '';
    }
  };

  const handleFocus = () => {
    // Only show dropdown if there are results and we're not showing a selected value
    if (!justSelectedRef.current && suburbs.length > 0 && searchText.length >= 2) {
      setIsOpen(true);
    }
  };

  const handleClear = () => {
    justSelectedRef.current = false;
    selectedSuburbRef.current = '';
    initializedRef.current = false;
    setSearchText('');
    setSuburbs([]);
    setIsOpen(false);
    onSelect({
      suburbID: 0,
      suburb: '',
      state: '',
      postCode: '',
      country: ''
    });
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={searchText}
          onChange={handleInputChange}
          onFocus={handleFocus}
          disabled={disabled}
          className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          placeholder={placeholder}
          autoComplete="off"
        />
        {searchText && !loading && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        {loading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
          </div>
        )}
      </div>

      {isOpen && suburbs.length > 0 && (
        <div className="absolute z-[9999] w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {suburbs.map((suburb) => (
            <button
              key={suburb.suburbID}
              type="button"
              onClick={() => handleSelect(suburb)}
              className="w-full px-4 py-3 text-left hover:bg-indigo-50 transition-colors border-b border-gray-100 last:border-b-0"
            >
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{suburb.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {suburb.stateAbbr} {suburb.postCode}, {suburb.country}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {isOpen && searchText.length >= 2 && suburbs.length === 0 && !loading && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center">
          <p className="text-sm text-gray-500">No suburbs found</p>
        </div>
      )}
    </div>
  );
}

export default SuburbSearch;